import API_BASE from "./apiConfig";
import React, { useState, useEffect, useRef } from "react";
import {
  FaEnvelope, FaLock, FaSignInAlt, FaExclamationCircle,
  FaChild, FaUserShield, FaChartLine, FaTrophy, FaCommentDots,
  FaUsers, FaCalendarAlt, FaCog
} from "react-icons/fa";
import { WeatherSunny24Regular, WeatherMoon24Regular } from "@fluentui/react-icons";
import { useAppConfig } from "./AppContext";
import { useTheme, getT, glassCard, getBrandColors } from "./ThemeContext";
import SixersLogo from "./Sixers_Logo.png";
import bg6 from "./Background images/6.jpg";
import bg8 from "./Background images/8.jpg";

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@500;600;700&family=Exo+2:wght@300;400;500;600;700;800&display=swap');

  @keyframes lp_fadeUp  { from{opacity:0;transform:translateY(26px)} to{opacity:1;transform:translateY(0)} }
  @keyframes lp_fadeIn  { from{opacity:0} to{opacity:1} }
  @keyframes lp_scan    { 0%{transform:translateY(-100%)} 100%{transform:translateY(100dvh)} }
  @keyframes lp_cardGlow {
    0%,100%{box-shadow:0 32px 80px rgba(0,0,0,0.15),0 0 22px rgba(247,206,11,0.05)}
    50%    {box-shadow:0 32px 80px rgba(0,0,0,0.25),0 0 44px rgba(247,206,11,0.15)}
  }
  @keyframes lp_spin      { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
  @keyframes lp_hex       { 0%,100%{transform:translateY(0) rotate(0deg);opacity:0.12} 50%{transform:translateY(-16px) rotate(5deg);opacity:0.24} }
  @keyframes lp_logoPulse { 0%,100%{filter:drop-shadow(0 0 15px rgba(247,206,11,0.3))} 50%{filter:drop-shadow(0 0 30px rgba(247,206,11,0.5))} }
  @keyframes lp_haloBlue   { 0%,100%{box-shadow:0 0 0 4px rgba(3,78,123,0.08),0 0 16px 4px rgba(3,78,123,0.18)} 50%{box-shadow:0 0 0 8px rgba(3,78,123,0.13),0 0 32px 10px rgba(3,78,123,0.36)} }
  @keyframes lp_haloYellow { 0%,100%{box-shadow:0 0 0 4px rgba(247,206,11,0.12),0 0 16px 4px rgba(247,206,11,0.28)} 50%{box-shadow:0 0 0 8px rgba(247,206,11,0.20),0 0 32px 10px rgba(247,206,11,0.55)} }

  .lp_input::placeholder{color:var(--text-muted);}
  .lp_tab {transition:all 0.24s cubic-bezier(.4,0,.2,1);}
  .lp_tab:hover{filter:brightness(1.10);}
  .lp_btn {transition:all 0.2s cubic-bezier(.4,0,.2,1);}
  .lp_btn:hover:not(:disabled){transform:translateY(-2px);filter:brightness(1.08);box-shadow: 0 8px 25px rgba(247,206,11,0.4);}
  .lp_btn:active:not(:disabled){transform:translateY(0);}
  .lp_feat{transition:all 0.2s; cursor: default;}
  .lp_wrap{transition:border-color 0.2s, background 0.2s;}
`;

const HEXES=[
  {x:5,y:7,s:1.3,d:0},{x:18,y:50,s:0.8,d:1.1},{x:62,y:10,s:1.1,d:0.6},{x:78,y:62,s:1.5,d:0.4},
  {x:42,y:78,s:0.7,d:1.7},{x:11,y:76,s:1.0,d:0.9},{x:86,y:27,s:0.9,d:1.4},{x:50,y:44,s:0.6,d:2.0},
];

function HexGrid({ color }) {
  return (
    <div style={{position:"absolute",inset:0,pointerEvents:"none",overflow:"hidden"}}>
      {HEXES.map((h,i)=>(
        <div key={i} style={{position:"absolute",left:`${h.x}%`,top:`${h.y}%`,width:64*h.s,height:64*h.s,animation:`lp_hex ${4+h.d}s ease-in-out ${h.d}s infinite`}}>
          <svg viewBox="0 0 100 100" style={{width:"100%",height:"100%"}}>
            <polygon points="50,2 93,26 93,74 50,98 7,74 7,26" fill="none" stroke={color} strokeWidth="1.2"/>
          </svg>
        </div>
      ))}
    </div>
  );
}

function Corners({color=getBrandColors().yellow,size=24}) {
  const a={position:"absolute"}, b=(e={})=>({position:"absolute",background:color,...e}), T=2.5;
  return(<>
    <div style={{...a,top:-1,left:-1,width:size,height:size}}><div style={b({top:0,left:0,width:size,height:T})}/><div style={b({top:0,left:0,width:T,height:size})}/></div>
    <div style={{...a,top:-1,right:-1,width:size,height:size}}><div style={b({top:0,right:0,width:size,height:T})}/><div style={b({top:0,right:0,width:T,height:size})}/></div>
    <div style={{...a,bottom:-1,left:-1,width:size,height:size}}><div style={b({bottom:0,left:0,width:size,height:T})}/><div style={b({bottom:0,left:0,width:T,height:size})}/></div>
    <div style={{...a,bottom:-1,right:-1,width:size,height:size}}><div style={b({bottom:0,right:0,width:size,height:T})}/><div style={b({bottom:0,right:0,width:T,height:size})}/></div>
  </>);
}

export default function LoginPage({ onLogin }) {
  const { isDark, toggleTheme } = useTheme();
  const T = getT(isDark);
  const gc = glassCard(T);
  
  const { config } = useAppConfig();
  const secondary  = getBrandColors().yellow;
  const orgName    = config.org_name    || "Sixers Sports Center";
  const orgTagline = config.org_tagline || "Academy Intelligence";
  const logoSrc    = config.logo_url    || SixersLogo;

  const [portal,  setPortal]  = useState("staff");
  const [email,   setEmail]   = useState("");
  const [password,setPassword]= useState("");
  const [error,   setError]   = useState("");
  const [loading, setLoading] = useState(false);
  const [bgIdx,   setBgIdx]   = useState(0);
  const [mounted, setMounted] = useState(false);
  const [pendingUser,  setPendingUser]  = useState(null);
  const [newPw,        setNewPw]        = useState("");
  const [confirmPw,    setConfirmPw]    = useState("");
  const [pwError,      setPwError]      = useState("");
  const [pwLoading,    setPwLoading]    = useState(false);
  const [waiverUser,   setWaiverUser]   = useState(null);
  const [waiverAgreed, setWaiverAgreed] = useState(false);
  const [waiverLoading,setWaiverLoading]= useState(false);
  const [waiverError,  setWaiverError]  = useState("");
  const waiverCanvasRef = useRef(null);
  const waiverDrawing   = useRef(false);
  const isParent = portal==="parent";
  const googleBtnRef = useRef(null);

  useEffect(()=>{
    const t=setTimeout(()=>setMounted(true),80);
    const iv=setInterval(()=>setBgIdx(p=>(p+1)%2),8000);
    return()=>{clearTimeout(t);clearInterval(iv);};
  },[]);

  // Google Sign-In init
  useEffect(()=>{
    if(!isParent) return;
    const clientId=process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if(!clientId||clientId==="YOUR_GOOGLE_CLIENT_ID_HERE") return;

    const doRender=()=>{
      if(!window.google||!googleBtnRef.current) return;
      googleBtnRef.current.innerHTML="";
      try{
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: async(credResp)=>{
            setError(""); setLoading(true);
            try{
              const res=await fetch(`${API_BASE}/parent-auth/google-login`,{
                method:"POST",
                headers:{"Content-Type":"application/json"},
                body:JSON.stringify({id_token:credResp.credential}),
              });
              const data=await res.json();
              if(data.success){
                if(isParent && !data.user.waiver_signed){ setWaiverUser(data.user); return; }
                onLogin(data.user);
              }
              else setError(data.message||"Google login failed.");
            }catch{ setError("Server unreachable."); }
            finally{ setLoading(false); }
          },
        });
        window.google.accounts.id.renderButton(googleBtnRef.current,{
          theme: isDark ? "filled_black" : "filled_blue",
          size:"large",
          width: 400,
          text:"signin_with",
        });
      }catch(e){ console.warn(e); }
    };
    const t=setTimeout(doRender,100);
    return()=>clearTimeout(t);
  },[isParent, isDark, onLogin]);

  const switchPortal=p=>{setPortal(p);setError("");setEmail("");setPassword("");};

  const handleSubmit=async(e)=>{
    e.preventDefault(); setError("");
    if(!email||!password){setError("Credentials required.");return;}
    setLoading(true);
    try{
      const ep=isParent?`${API_BASE}/parent-auth/login`:`${API_BASE}/auth/login`;
      const res=await fetch(ep,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({email,password})});
      const data=await res.json();
      if(data.success){
        if(data.user.must_change_password){ setPendingUser(data.user); return; }
        if(isParent && !data.user.waiver_signed){ setWaiverUser(data.user); return; }
        onLogin(data.user);
      }
      else setError(data.message||"Invalid credentials.");
    }catch{setError("Server connection failed.");}
    finally{setLoading(false);}
  };

  const handleChangePassword=async(e)=>{
    e.preventDefault(); setPwError("");
    if(newPw.length<6){ setPwError("Password must be at least 6 characters."); return; }
    if(newPw!==confirmPw){ setPwError("Passwords do not match."); return; }
    setPwLoading(true);
    try{
      const res=await fetch(`${API_BASE}/parent-auth/change-password`,{
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({parent_id:pendingUser.parent_id,new_password:newPw}),
      });
      const data=await res.json();
      if(data.success){
        if(isParent && !data.user.waiver_signed){ setWaiverUser(data.user); return; }
        onLogin(data.user);
      }
      else setPwError(data.message||"Failed to update password.");
    }catch{ setPwError("Server connection failed."); }
    finally{ setPwLoading(false); }
  };

  const F = "'Exo 2', sans-serif";
  const D = "'Rajdhani', sans-serif";

  // Force password change screen
  if(pendingUser){
    return(
      <>
        <style>{CSS}</style>
        <div style={{minHeight:"100dvh",display:"flex",alignItems:"center",justifyContent:"center",background:isDark?"#0d1117":"#f0f4f8"}}>
          <div style={{...gc,width:"100%",maxWidth:420,padding:"40px 36px",borderRadius:20,position:"relative"}}>
            <div style={{textAlign:"center",marginBottom:28}}>
              <img src={logoSrc} alt="logo" style={{width:64,height:64,objectFit:"contain",marginBottom:12}}/>
              <div style={{fontSize:20,fontWeight:700,color:T.text,fontFamily:D}}>Set Your New Password</div>
              <div style={{fontSize:13,color:T.muted,marginTop:6}}>
                Welcome, {pendingUser.full_name}! Please set a personal password before continuing.
              </div>
            </div>
            <form onSubmit={handleChangePassword}>
              <div style={{marginBottom:16}}>
                <label style={{fontSize:12,fontWeight:600,color:T.muted,letterSpacing:"0.08em",display:"block",marginBottom:6}}>NEW PASSWORD</label>
                <div style={{display:"flex",alignItems:"center",gap:10,background:T.inputBg||"rgba(0,0,0,0.05)",border:`1px solid ${T.border}`,borderRadius:10,padding:"11px 14px"}}>
                  <FaLock style={{color:T.muted,flexShrink:0}}/>
                  <input className="lp_input" type="password" value={newPw} onChange={e=>setNewPw(e.target.value)}
                    placeholder="Min 6 characters" autoFocus
                    style={{flex:1,border:"none",outline:"none",background:"transparent",color:T.text,fontSize:14}}/>
                </div>
              </div>
              <div style={{marginBottom:20}}>
                <label style={{fontSize:12,fontWeight:600,color:T.muted,letterSpacing:"0.08em",display:"block",marginBottom:6}}>CONFIRM PASSWORD</label>
                <div style={{display:"flex",alignItems:"center",gap:10,background:T.inputBg||"rgba(0,0,0,0.05)",border:`1px solid ${T.border}`,borderRadius:10,padding:"11px 14px"}}>
                  <FaLock style={{color:T.muted,flexShrink:0}}/>
                  <input className="lp_input" type="password" value={confirmPw} onChange={e=>setConfirmPw(e.target.value)}
                    placeholder="Re-enter password"
                    style={{flex:1,border:"none",outline:"none",background:"transparent",color:T.text,fontSize:14}}/>
                </div>
              </div>
              {pwError&&<div style={{display:"flex",alignItems:"center",gap:8,color:"#ef4444",fontSize:13,marginBottom:14,background:"rgba(239,68,68,0.08)",padding:"10px 14px",borderRadius:8}}>
                <FaExclamationCircle/>{pwError}
              </div>}
              <button type="submit" disabled={pwLoading} className="lp_btn"
                style={{width:"100%",padding:"14px",borderRadius:12,border:"none",cursor:pwLoading?"not-allowed":"pointer",
                  background:getBrandColors().yellow,color:"#1a1a1a",fontWeight:700,fontSize:15,fontFamily:D}}>
                {pwLoading?"Saving...":"Set Password & Continue"}
              </button>
            </form>
          </div>
        </div>
      </>
    );
  }

  // ── Canvas signature helpers ──────────────────────────────────────────────
  const waiverPos=(e,canvas)=>{
    const r=canvas.getBoundingClientRect();
    if(e.touches){ return {x:e.touches[0].clientX-r.left, y:e.touches[0].clientY-r.top}; }
    return {x:e.clientX-r.left, y:e.clientY-r.top};
  };
  const waiverStart=(e)=>{
    e.preventDefault();
    const canvas=waiverCanvasRef.current; if(!canvas) return;
    const ctx=canvas.getContext("2d");
    const {x,y}=waiverPos(e,canvas);
    ctx.beginPath(); ctx.moveTo(x,y);
    waiverDrawing.current=true;
  };
  const waiverMove=(e)=>{
    e.preventDefault();
    if(!waiverDrawing.current) return;
    const canvas=waiverCanvasRef.current; if(!canvas) return;
    const ctx=canvas.getContext("2d");
    const {x,y}=waiverPos(e,canvas);
    ctx.lineTo(x,y); ctx.stroke();
  };
  const waiverEnd=()=>{ waiverDrawing.current=false; };
  const clearSignature=()=>{
    const canvas=waiverCanvasRef.current; if(!canvas) return;
    const ctx=canvas.getContext("2d");
    ctx.clearRect(0,0,canvas.width,canvas.height);
  };
  const isCanvasBlank=()=>{
    const canvas=waiverCanvasRef.current; if(!canvas) return true;
    return !canvas.getContext("2d").getImageData(0,0,canvas.width,canvas.height).data.some(v=>v!==0);
  };

  const handleSignWaiver=async()=>{
    setWaiverError("");
    if(!waiverAgreed){ setWaiverError("Please check the agreement box before signing."); return; }
    if(isCanvasBlank()){ setWaiverError("Please provide your signature above."); return; }
    const sig=waiverCanvasRef.current.toDataURL("image/png");
    setWaiverLoading(true);
    try{
      const res=await fetch(`${API_BASE}/parent-auth/sign-waiver`,{
        method:"POST", headers:{"Content-Type":"application/json"},
        body:JSON.stringify({parent_id:waiverUser.parent_id, signature:sig}),
      });
      const data=await res.json();
      if(data.success) onLogin(data.user);
      else setWaiverError(data.message||"Failed to save signature.");
    }catch{ setWaiverError("Server connection failed."); }
    finally{ setWaiverLoading(false); }
  };

  // ── Waiver screen ──────────────────────────────────────────────────────────
  if(waiverUser){
    const bg=isDark?"#0d1117":"#f0f4f8";
    const cardStyle={...gc,width:"100%",maxWidth:780,borderRadius:20,overflow:"hidden",display:"flex",flexDirection:"column",maxHeight:"92vh"};
    // Waiver copy reads the tenant's org name from config (orgName, above) instead
    // of hardcoding "Sixers" into legally-binding text — every business using this
    // app gets its own name in the waiver automatically.
    const sections=[
      {title:"Waiver & Release",text:`I acknowledge that by engaging in sports, recreation, and other activities at ${orgName} (referred to as "the Academy"), I am doing so voluntarily and with an understanding of the risks involved. In consideration of being allowed to participate and for valuable consideration received, I hereby release and discharge the Academy, its owners, directors, officers, employees, agents, volunteers, participants, and all other individuals or entities acting on their behalf (collectively referred to as the "Releasee"), from any liability arising from negligence, on behalf of myself, my children, parents, heirs, assigns, personal representatives, and estate.`},
      {title:"Acknowledgment of Risk",text:"I understand that participation in activities at the Academy involves inherent risks, including physical or emotional injury, paralysis, permanent disability, death, and property damage. These risks include but are not limited to bodily injuries such as broken bones, torn ligaments, and bruises, as well as property damage caused by various factors including contact with equipment or other participants, or hazardous conditions on the premises. I acknowledge that such risks cannot be entirely eliminated, even with safety measures in place."},
      {title:"Assumption of Risk",text:"I expressly accept and assume all inherent risks associated with participating in the Academy's activities, including those arising from the negligence of the Releasee. My participation is voluntary, and I agree to cease participation immediately if I believe conditions are unsafe."},
      {title:"Indemnification",text:"I agree to indemnify and hold harmless the Releasee from any and all liabilities or claims made by other individuals or entities as a result of my participation in the Academy's activities."},
      {title:"Insurance Coverage",text:"I represent that I have sufficient insurance coverage for any injury or damage that may occur during my participation in the Academy's activities, or I agree to bear the costs myself."},
      {title:"Photograph Release",text:"I grant the Academy, its licensees, and contractors the right to photograph, film, or videotape me during activities for commercial, news, or other purposes, without compensation."},
      {title:"Exclusive Coaching",text:"By signing this waiver, the undersigned agrees that they will not allow any other coaches, trainers, or individuals to conduct coaching sessions, training, or any related activities at the Academy's premises without prior written consent from Academy management."},
      {title:"Membership / Coaching Program Cancellation",text:"A 60-day written notice via email is required to cancel membership and/or coaching programs. No partial refunds will be issued, and membership and/or coaching program(s) cancellations do not affect participation in pre-registered leagues or tournaments."},
      {title:"Minor Participant Consent",text:"As the parent or guardian with legal responsibility, I consent to the terms of this Agreement and release and indemnify the Releasees from any liability arising from my child's involvement in the Academy's activities."},
      {title:"Electronic Communication Consent",text:`I consent to receive communications from Academy Management via mobile phone, email, and other online channels. I authorize ${orgName} to initiate payments from my card account according to the terms of my agreement with them.`},
      {title:"Disclosure Policies",text:"Participants must return all equipment after use. Metal cleats and actions causing damage are prohibited. Pets (except ADA service animals) are prohibited. No food or drinks inside playing nets. Violence, abusive language, or physical aggression will not be tolerated. A 60-day written notice is required to cancel memberships or coaching programs."},
      {title:"Parental Pledge",text:"By signing this document, I acknowledge that I may be deemed to have waived my right to bring a lawsuit against the Releasees for any negligence-related claims. I confirm that I have read and understood this document, and my electronic signature is legally binding."},
    ];
    return(
      <>
        <style>{CSS}</style>
        <div style={{minHeight:"100dvh",display:"flex",alignItems:"center",justifyContent:"center",background:bg,padding:"16px 8px"}}>
          <div style={cardStyle}>
            {/* Header */}
            <div style={{background:getBrandColors().blue,padding:"20px 28px",display:"flex",alignItems:"center",gap:14,flexShrink:0}}>
              <img src={logoSrc} alt="logo" style={{width:44,height:44,objectFit:"contain"}}/>
              <div>
                <div style={{color:getBrandColors().yellow,fontWeight:700,fontSize:17,fontFamily:D}}>General Liability Waiver & Disclosure</div>
                <div style={{color:"rgba(255,255,255,0.75)",fontSize:12}}>{orgName} — Please read carefully before signing</div>
              </div>
            </div>

            {/* Scrollable body */}
            <div style={{overflowY:"auto",flex:1,padding:"20px 28px"}}>
              <div style={{fontSize:13,color:T.muted,marginBottom:18,lineHeight:1.5,background:isDark?"rgba(247,206,11,0.06)":"rgba(3,78,123,0.05)",padding:"12px 16px",borderRadius:10,borderLeft:`3px solid ${getBrandColors().yellow}`}}>
                By electronically signing you are agreeing to the terms and conditions set forth within this waiver and that your electronic signature is a legally binding form of agreement.
              </div>
              {sections.map((s,i)=>(
                <div key={i} style={{marginBottom:16}}>
                  <div style={{fontWeight:700,fontSize:13,color:T.text,marginBottom:4,fontFamily:D}}>{s.title}</div>
                  <div style={{fontSize:12.5,color:T.muted,lineHeight:1.65}}>{s.text}</div>
                </div>
              ))}

              {/* Signature pad */}
              <div style={{marginTop:24,borderTop:`1px solid ${T.border}`,paddingTop:20}}>
                <div style={{fontWeight:700,fontSize:14,color:T.text,marginBottom:4,fontFamily:D}}>Your Signature</div>
                <div style={{fontSize:12,color:T.muted,marginBottom:10}}>Sign below using your mouse or finger</div>
                <div style={{position:"relative",border:`2px solid ${T.border}`,borderRadius:10,overflow:"hidden",background:isDark?"#1a1a2e":"#fff",cursor:"crosshair"}}>
                  <canvas
                    ref={waiverCanvasRef}
                    width={700} height={220}
                    style={{display:"block",width:"100%",height:220,touchAction:"none"}}
                    onMouseDown={waiverStart} onMouseMove={waiverMove} onMouseUp={waiverEnd} onMouseLeave={waiverEnd}
                    onTouchStart={waiverStart} onTouchMove={waiverMove} onTouchEnd={waiverEnd}
                  />
                  <div style={{position:"absolute",bottom:6,left:0,right:0,borderTop:`1px dashed ${T.border}`,margin:"0 16px",pointerEvents:"none"}}/>
                  <div style={{position:"absolute",bottom:10,left:20,fontSize:11,color:T.muted,pointerEvents:"none",opacity:0.5}}>Sign here</div>
                </div>
                <button onClick={clearSignature} style={{marginTop:6,fontSize:11,color:T.muted,background:"none",border:"none",cursor:"pointer",padding:0,textDecoration:"underline"}}>Clear signature</button>

                {/* After canvas renders, set up stroke style */}
                <div ref={el=>{
                  if(el&&waiverCanvasRef.current){
                    const ctx=waiverCanvasRef.current.getContext("2d");
                    ctx.strokeStyle=getBrandColors().blue; ctx.lineWidth=2.5; ctx.lineCap="round"; ctx.lineJoin="round";
                  }
                }} style={{display:"none"}}/>
              </div>

              {/* Agreement checkbox */}
              <div style={{marginTop:18,display:"flex",alignItems:"flex-start",gap:10}}>
                <input type="checkbox" id="waiverCheck" checked={waiverAgreed} onChange={e=>setWaiverAgreed(e.target.checked)}
                  style={{marginTop:2,width:16,height:16,accentColor:getBrandColors().blue,flexShrink:0}}/>
                <label htmlFor="waiverCheck" style={{fontSize:12.5,color:T.text,lineHeight:1.6,cursor:"pointer"}}>
                  I, <strong>{waiverUser.full_name}</strong>, confirm that I have read, understood, and agree to abide by the above Waiver & Disclosure on behalf of myself and my family. I understand my electronic signature is legally binding.
                </label>
              </div>

              {/* Error */}
              {waiverError&&<div style={{display:"flex",alignItems:"center",gap:8,color:"#ef4444",fontSize:13,marginTop:14,background:"rgba(239,68,68,0.08)",padding:"10px 14px",borderRadius:8}}>
                <FaExclamationCircle/>{waiverError}
              </div>}

              {/* Sign button */}
              <button onClick={handleSignWaiver} disabled={waiverLoading} className="lp_btn"
                style={{marginTop:20,marginBottom:8,width:"100%",padding:"14px",borderRadius:12,border:"none",cursor:waiverLoading?"not-allowed":"pointer",
                  background:getBrandColors().blue,color:"#fff",fontWeight:700,fontSize:15,fontFamily:D}}>
                {waiverLoading?"Saving...":"I Agree & Sign — Enter Portal"}
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  const staffFeatures=[
    {icon:FaUsers,       text:"Full performance analytics",  tag:"CORE"},
    {icon:FaCalendarAlt, text:"Attendance & scheduling",     tag:"LIVE"},
    {icon:FaTrophy,      text:"KPI scoring engine",          tag:"STATS"},
    {icon:FaCog,         text:"Enterprise management",      tag:"SEC" },
  ];
  const parentFeatures=[
    {icon:FaChartLine,   text:"Live progress tracking",      tag:"LIVE"},
    {icon:FaTrophy,      text:"Technical category stats",   tag:"STATS"},
    {icon:FaCommentDots, text:"Coach session feedback",      tag:"FEED"},
    {icon:FaChild,       text:"Student profile control",    tag:"ACCT"},
  ];
  const features = isParent ? parentFeatures : staffFeatures;
  

  return(
    <>
      <style>{CSS}</style>
      <div style={{
        minHeight:"100dvh", fontFamily:F, overflow:"hidden", position:"relative", 
        background: T.pageBg, display:"flex", transition: "background 0.5s ease"
      }}>

        {/* Global CSS Variables for the theme */}
        <div style={{ display: 'none' }}>
           <style>{`
             :root {
               --text-muted: ${T.textMuted};
             }
           `}</style>
        </div>

        {/* Background Images with Theme-based Overlay */}
        {[bg6,bg8].map((src,i)=>(
          <div key={i} style={{position:"absolute",inset:0,zIndex:0,backgroundImage:`url(${src})`,backgroundSize:"cover",backgroundPosition:"center 25%",opacity:bgIdx===i ? (isDark ? 0.45 : 0.35) : 0, transition:"opacity 1.5s ease-in-out"}}/>
        ))}

        <div style={{
          position:"absolute",inset:0,zIndex:1,
          background: isDark 
            ? "linear-gradient(135deg, rgba(2,13,24,0.95) 0%, rgba(2,13,24,0.85) 100%)"
            : "linear-gradient(135deg, rgba(240,246,255,0.55) 0%, rgba(255,255,255,0.45) 100%)"
        }}/>
        
        <div style={{position:"absolute",inset:0,zIndex:2}}><HexGrid color={isDark ? "rgba(247,206,11,0.22)" : "rgba(3,78,123,0.12)"}/></div>
        
        {/* Scanned Lines / Scan effect */}
        <div style={{position:"absolute",inset:0,zIndex:3,pointerEvents:"none",overflow:"hidden"}}>
          <div style={{position:"absolute",left:0,right:0,height:2,background: isDark ? "rgba(247,206,11,0.08)" : "rgba(3,78,123,0.04)", animation:"lp_scan 12s linear infinite"}}/>
          <div style={{position:"absolute",inset:0,backgroundImage: isDark ? "repeating-linear-gradient(0deg,transparent,transparent 2px,rgba(255,255,255,0.015) 2px,rgba(255,255,255,0.015) 4px)" : "repeating-linear-gradient(0deg,transparent,transparent 2px,rgba(0,0,0,0.01) 2px,rgba(0,0,0,0.01) 4px)"}}/>
        </div>

        {/* Theme Toggle Button — same rectangular glass-pill style as the post-login sidebar's toggle:
            translucent tinted background, no solid white fill, so both screens read as one design. */}
        <button
          onClick={toggleTheme}
          title={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}
          style={{
            position: "absolute", top: 24, left: 24, zIndex: 100,
            height: 36, padding: "0 14px", borderRadius: "10px",
            background: isDark ? "rgba(255,255,255,0.06)" : "rgba(3,78,123,0.06)",
            border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(3,78,123,0.14)",
            cursor: "pointer", display: "flex", alignItems: "center", gap: 8,
            color: isDark ? "rgba(255,255,255,0.80)" : getBrandColors().blue,
            transition: "background 0.15s",
            fontSize: 12.5, fontWeight: 700, letterSpacing: "0.02em",
          }}
          onMouseEnter={e => { e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.11)" : "rgba(3,78,123,0.11)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.06)" : "rgba(3,78,123,0.06)"; }}
        >
          {isDark ? <WeatherSunny24Regular fontSize={16}/> : <WeatherMoon24Regular fontSize={16}/>}
          {isDark ? "Light Mode" : "Dark Mode"}
        </button>

        <div style={{position:"relative",zIndex:6,width:"100%",display:"flex",minHeight:"100dvh",opacity:mounted?1:0,transition:"opacity 0.6s ease"}}>

          {/* LEFT: Branding & Features */}
          <div style={{flex:"0 0 50%",display:"flex",flexDirection:"column",justifyContent:"center",padding:"0 64px"}}>
            <div style={{display:"flex",alignItems:"center",gap:24,marginBottom:20}}>
              <div style={{position:"relative",flexShrink:0,width:134,height:134}}>
                {/* Halo ring — circular container with inner padding so logo never touches edge */}
                <div style={{
                  position:"absolute", inset:0, borderRadius:"50%",
                  border: isDark ? "2.5px solid rgba(247,206,11,0.65)" : "2.5px solid rgba(3,78,123,0.50)",
                  background: isDark ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.88)",
                  animation: isDark ? "lp_haloYellow 2.6s ease-in-out infinite" : "lp_haloBlue 2.6s ease-in-out infinite",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  zIndex:1,
                }}>
                  <img src={logoSrc} alt={orgName} style={{width:96,objectFit:"contain",animation:"lp_logoPulse 3s ease-in-out infinite"}}/>
                </div>
                {/* Spinning dashed outer ring */}
                <div style={{position:"absolute",inset:-20,border:`2px dashed ${isDark ? "rgba(247,206,11,0.2)" : "rgba(3,78,123,0.1)"}`,borderRadius:"50%",animation:"lp_spin 30s linear infinite"}}/>
              </div>
              <div>
                <h1 style={{fontFamily:D,fontSize:32,fontWeight:900,color:T.textPrimary,margin:0,letterSpacing:"0.06em"}}>
                  {orgName.toUpperCase()}
                </h1>
                <div style={{display:"flex",alignItems:"center",gap:9,marginTop:8}}>
                  <div style={{width:26,height:3,background:T.yellow,borderRadius:2}}/>
                  <span style={{fontFamily:F,fontSize:12,fontWeight:700,color:T.yellow,letterSpacing:"0.16em",textTransform:"uppercase"}}>
                    {isParent ? "Parent Portal" : orgTagline}
                  </span>
                </div>
              </div>
            </div>

            <p style={{fontSize:16,fontWeight:500,color:T.textSub,lineHeight:1.4,margin:"0 0 40px",maxWidth:480 }}>
              {isParent ? "Real-time performance analytics and coach feedback for your child." : orgTagline}
            </p>

            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              {features.map(({icon:Icon,text,tag},i)=>(
                <div key={i} className="lp_feat" style={{
                  display:"flex",alignItems:"center",gap:16,padding:"16px",borderRadius:12,
                  background: isDark ? "rgba(255,255,255,0.03)" : "rgba(3,78,123,0.04)",
                  border: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(3,78,123,0.08)",
                  animation:`lp_fadeUp 0.4s ease ${0.5+i*0.1}s both`,
                }}>
                  <div style={{width:40,height:40,borderRadius:10,background: isDark ? "rgba(247,206,11,0.08)" : "rgba(3,78,123,0.05)",display:"flex",alignItems:"center",justifyContent:"center"}}>
                    <Icon size={18} style={{color:isDark ? T.yellow : T.blue}}/>
                  </div>
                  <span style={{flex:1,fontSize:15,fontWeight:600,color:T.textPrimary}}>{text}</span>
                  <span style={{fontFamily:D,fontSize:11,fontWeight:700,color:isDark ? T.yellow : T.blue, opacity:0.6}}>{tag}</span>
                </div>
              ))}
            </div>

            <div style={{marginTop:40,display:"flex",alignItems:"center",gap:12}}>
              <span style={{fontSize:14,color:T.textMuted}}>{isParent?"Staff member?":"Registered parent?"}</span>
              <button onClick={()=>switchPortal(isParent?"staff":"parent")} style={{background:"none",border:"none",padding:0,cursor:"pointer",fontSize:14,fontWeight:800,color:T.yellow,textTransform:"uppercase",letterSpacing:"0.05em"}}>
                {isParent?"Staff login →":"Parent login →"}
              </button>
            </div>
          </div>

          {/* RIGHT: Login Card */}
          <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",paddingRight:64}}>
            <div style={{width:"100%",maxWidth:480,position:"relative"}}>
              
              <div style={{ ...gc, padding:"48px 40px", animation:"lp_cardGlow 6s ease-in-out infinite", position:"relative" }}>
                <Corners color={T.yellow} size={28}/>

                {/* Tabs */}
                <div style={{display:"flex",gap:5,marginBottom:32,padding:5,background: isDark ? "rgba(0,0,0,0.3)" : "rgba(3,78,123,0.05)", borderRadius:12, border: isDark ? "1px solid rgba(255,255,255,0.05)" : "1px solid rgba(3,78,123,0.1)"}}>
                  {[{id:"staff",label:"Staff Login",Icon:FaUserShield},{id:"parent",label:"Parent Login",Icon:FaChild}].map(p=>{
                    const on=portal===p.id;
                    return(
                      <button key={p.id} className="lp_tab" onClick={()=>switchPortal(p.id)} style={{
                        flex:1,padding:"12px 8px",borderRadius:9,border:"none",cursor:"pointer",
                        display:"flex",alignItems:"center",justifyContent:"center",gap:9,
                        fontFamily:D,fontSize:15,fontWeight:700,letterSpacing:"0.06em",
                        background:on?`linear-gradient(135deg,${T.yellow},#d9b60a)`:"transparent",
                        color:on?T.blueDark:T.textMuted,
                        boxShadow:on?`0 4px 20px rgba(247,206,11,0.3)`:"none",
                      }}><p.Icon size={16}/>{p.label}</button>
                    );
                  })}
                </div>

                {/* Heading */}
                <div style={{marginBottom:32}}>
                  <h2 style={{fontFamily:D,fontSize:38,fontWeight:900,color:T.textPrimary,margin:0,letterSpacing:"0.04em"}}>
                    {isParent?"PARENT LOGIN":"SECURE SIGN IN"}
                  </h2>
                  <p style={{fontSize:15,color:T.textSub,marginTop:8, fontWeight:500}}>
                    Enter your credentials to access the {isParent?"Parent Intelligence Dashboard":"Management Suite"}.
                  </p>
                </div>

                {error&&(
                  <div style={{display:"flex",alignItems:"center",gap:10,padding:"14px 18px",borderRadius:10,marginBottom:24,background:T.redBg,border:`1px solid ${T.redBdr}`,color:T.red,fontSize:14,fontWeight:700}}>
                    <FaExclamationCircle size={16}/>{error}
                  </div>
                )}

                {isParent&&(
                  <div style={{marginBottom:24}}>
                    <div ref={googleBtnRef} style={{width:"100%",borderRadius:10,overflow:"hidden"}}/>
                    <div style={{display:"flex",alignItems:"center",gap:12,margin:"24px 0"}}>
                      <div style={{flex:1,height:1,background:T.divider}}/>
                      <span style={{fontSize:12,fontWeight:800,color:T.textMuted,letterSpacing:"0.1em"}}>OR USE EMAIL</span>
                      <div style={{flex:1,height:1,background:T.divider}}/>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} style={{display:"flex",flexDirection:"column",gap:20}}>
                  <div>
                    <label style={{display:"block",marginBottom:10,fontFamily:D,fontSize:13,fontWeight:700,letterSpacing:"0.12em",color:T.textLabel}}>EMAIL ADDRESS</label>
                    <div className="lp_wrap" style={{display:"flex",alignItems:"center",gap:14,background:T.inputBg,border:`1.5px solid ${T.inputBdr}`,borderRadius:12,padding:"16px 20px"}}>
                      <FaEnvelope size={18} style={{color:isDark ? "rgba(255,255,255,0.4)" : "rgba(3,78,123,0.3)"}}/>
                      <input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="Email"
                        style={{border:"none",outline:"none",background:"transparent",color:T.inputText,fontSize:16,width:"100%",fontWeight:500}}/>
                    </div>
                  </div>
                  <div>
                    <label style={{display:"block",marginBottom:10,fontFamily:D,fontSize:13,fontWeight:700,letterSpacing:"0.12em",color:T.textLabel}}>PASSWORD</label>
                    <div className="lp_wrap" style={{display:"flex",alignItems:"center",gap:14,background:T.inputBg,border:`1.5px solid ${T.inputBdr}`,borderRadius:12,padding:"16px 20px"}}>
                      <FaLock size={18} style={{color:isDark ? "rgba(255,255,255,0.4)" : "rgba(3,78,123,0.3)"}}/>
                      <input type="password" value={password} onChange={e=>setPassword(e.target.value)} placeholder="••••••••"
                        style={{border:"none",outline:"none",background:"transparent",color:T.inputText,fontSize:16,width:"100%",fontWeight:500}}/>
                    </div>
                  </div>
                  
                  <button type="submit" className="lp_btn" disabled={loading} style={{
                    marginTop:12,padding:"18px",borderRadius:12,border:"none",
                    fontFamily:D,fontSize:18,fontWeight:900,letterSpacing:"0.1em",
                    cursor:loading?"not-allowed":"pointer",
                    background:loading?"rgba(247,206,11,0.5)":`linear-gradient(135deg,${T.yellow},#d9b60a)`,
                    color:T.blueDark,boxShadow:`0 8px 30px rgba(247,206,11,0.3)`,display:"flex",alignItems:"center",justifyContent:"center",gap:12
                  }}>
                    {loading ? "AUTHENTICATING..." : <>{isParent?<FaChild size={20}/>:<FaSignInAlt size={20}/>} SECURE LOGIN</>}
                  </button>
                </form>

                <div style={{marginTop:32,textAlign:"center", color:T.textMuted, fontSize:13, fontWeight:500}}>
                   Trouble signing in? Contact academy support.
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Status */}
        <div style={{
          position:"absolute",bottom:0,left:0,right:0,zIndex:8,height:40,
          display:"flex",alignItems:"center",padding:"0 40px",gap:20,
          background: isDark ? "rgba(0,0,0,0.5)" : "rgba(255,255,255,0.8)",
          borderTop: isDark ? "1px solid rgba(255,255,255,0.05)" : "1px solid rgba(3,78,123,0.1)",
          backdropFilter:"blur(12px)",WebkitBackdropFilter:"blur(12px)"
        }}>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <div style={{width:8,height:8,borderRadius:"50%",background:"#4ade80",boxShadow:"0 0 10px rgba(74,222,128,0.5)"}}/>
            <span style={{fontFamily:D,fontSize:11,color:T.textMuted,letterSpacing:"0.12em"}}>SYSTEM SECURITY COMPLIANT</span>
          </div>
          <div style={{flex:1}}/>
          <span style={{fontFamily:D,fontSize:11,color:T.textLabel,letterSpacing:"0.08em"}}>
            {orgName.toUpperCase()} · v2.5 · SECURED END-TO-END
          </span>
        </div>
      </div>
    </>
  );
}
